import React, {
	useContext,
	ReactNode,
	useEffect,
	useState,
	RefAttributes,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocalStorage } from '@mantine/hooks';
import { AppError, ForeignLanguage, Language } from '@/types/types';
import { Link, LinkProps } from 'react-router-dom';
import { getFileSrc, isAppError } from '@/utils/utilities';
import { Typography } from '@mantine/core';
import parse, { DOMNode, Element, domToReact } from 'html-react-parser';
import ContentApi, { ContentType } from '@/api/ContentApi';
import LANGUAGES from '@/configs/languages';
import httpClient from '@/api/httpClient';
import APP_CONFIG from '@/configs/appConfig';
import moment from 'moment';
import 'moment/dist/locale/pl';
import {
	AppContent,
	checkIfExist,
	getText,
	getEntityText,
} from '@/utils/contentLookup';

export enum ErrorCode {
	GENERIC = 'genericError',
	USER_NOT_FOUND = 'userNotFound',
	USER_NOT_ACTIVE = 'userNotActive',
	USER_ALREADY_EXISTS = 'userAlreadyExists',
	DUPLICATED_EMAIL = 'duplicatedEmail',
	MISSING_FIELD = 'missingField',
	EMAIL_NOT_FOUND = 'emailNotFound',
	INVALID_EMAIL = 'emailIsNotValid',
	INVALID_PASSWORD = 'invalidPassword',
	INVALID_PHONE_NUMBER = 'phoneNumberIsNotValid',
	PASSWORD_TOO_WEAK = 'passwordTooWeak',
	DIFFERENT_PASSWORDS = 'differentPasswords',
	DIFFERENT_EMAILS = 'differentEmails',
	INVALID_REFRESH_TOKEN = 'invalidRefreshToken',
	REFRESH_TOKEN_EXPIRED = 'refreshTokenExpired',
	REFRESH_TOKEN_MALFORMED = 'refreshTokenMalformed',
	INVALID_TOKEN = 'invalidToken',
	INVALID_HASH = 'invalidHash',
	INVALID_POSTAL_CODE = 'invalidPostalCode',
	TOO_MANY_REQUESTS = 'tooManyRequests',
	INVALID_EMAIL_DOMAIN = 'invalidEmailDomain',
	REGISTRATION_FAILED = 'registrationFailed',
}

const errorSlug = 'error.';

export interface ContentContext {
	getContent: (
		slug: string,
		options?: {
			links?: Record<
				string,
				LinkProps & React.RefAttributes<HTMLAnchorElement>
			>;
			vars?: Record<string, string | number>;
		}
	) => string;
	language: Language;
	setLanguage: (lang: Language) => void;
	getErrorMessage: (codes: AppError['stack']) => string;
	getMutationErrorMessage: (error: unknown) => string;
	getEntityText: <
		TObj extends {
			translations: Record<
				ForeignLanguage,
				Partial<Record<keyof TObj, string>>
			> | null;
		},
	>(
		obj: TObj,
		key: keyof TObj
	) => string;
}

type GetElementOptions = {
	links?: Record<string, LinkProps & RefAttributes<HTMLAnchorElement>>;
	vars?: Record<string, string | number>;
};

const ContentContext = React.createContext<ContentContext>(null!);

export const useContent = () => useContext(ContentContext);

export const ContentProvider = ({ children }: { children: ReactNode }) => {
	const [content, setContent] = useLocalStorage<AppContent>({
		key: ContentApi.queryKey,
		defaultValue: {},
		getInitialValueInEffect: false,
	});

	const contentQuery = useQuery({
		queryKey: [ContentApi.queryKey],
		queryFn: ContentApi.getAll,
		staleTime: 1000 * 60 * 5,
		select: (content) =>
			content.reduce<AppContent>((acc, curr) => {
				acc[curr.slug] = curr;
				return acc;
			}, {}),
	});

	useEffect(() => {
		if (!contentQuery.data) return;

		setContent(contentQuery.data);
	}, [contentQuery.data]);

	const [language, setLanguage] = useLocalStorage<Language>({
		key: 'language',
		defaultValue: APP_CONFIG.DEFAULT_LANGUAGE,
		getInitialValueInEffect: false,
	});

	const [_, setRefresher] = useState(false);
	const refreshApp = () => setRefresher((prev) => !prev);

	useEffect(() => {
		const code = LANGUAGES[language].code;

		moment.locale(code);
		document.documentElement.setAttribute('lang', code);
		httpClient.defaults.headers['Accept-Language'] = language.toLowerCase();

		refreshApp();
	}, [language]);

	const isDefaultLanguage = language === APP_CONFIG.DEFAULT_LANGUAGE;

	const checkIfExistLocal = (slug: string) =>
		checkIfExist(content, slug, language, APP_CONFIG.DEFAULT_LANGUAGE);

	const getEntityTextLocal: ContentContext['getEntityText'] = (obj, key) =>
		getEntityText(obj, key, language, APP_CONFIG.DEFAULT_LANGUAGE);

	const getTextLocal = (
		slug: string,
		vars: Record<string, string | number> = {}
	) => getText(content, slug, language, APP_CONFIG.DEFAULT_LANGUAGE, vars);

	const getElement = (slug: string, options?: GetElementOptions) => {
		const raw = getTextLocal(slug, options?.vars);

		return (
			<Typography>
				{parse(raw, {
					replace: (domNode) => {
						const node = domNode as Element;

						if (
							node.name === 'a' &&
							options?.links &&
							options?.links[node.attribs.href]
						) {
							return (
								<Link {...options.links[node.attribs.href]}>
									{domToReact(node.children as DOMNode[])}
								</Link>
							);
						}
					},
				})}
			</Typography>
		);
	};

	const getImage = (slug: string) => {
		return getFileSrc(
			isDefaultLanguage
				? content[slug].image?.path
				: content[slug].translations[language]?.image?.path
		);
	};

	const getContent: ContentContext['getContent'] = (slug, options) => {
		if (!checkIfExistLocal(slug)) return slug;

		switch (content[slug].type) {
			case ContentType.TEXT:
				return getTextLocal(slug, options?.vars);
			case ContentType.ELEMENT:
				return getElement(slug, options) as unknown as string;
			case ContentType.IMAGE:
				return getImage(slug);
			default:
				return slug;
		}
	};

	const codeToMessage = (code: ErrorCode | string) => {
		const isErrorCode = Object.values(ErrorCode).includes(code as ErrorCode);
		if (!isErrorCode) return code;

		const exists = checkIfExistLocal(`${errorSlug}${code}`);
		if (!exists) throw new Error(`ERROR_NOT_FOUND: ${code}`);

		return getTextLocal(`${errorSlug}${code}`);
	};

	const getErrorMessage: ContentContext['getErrorMessage'] = (codes) => {
		const errorMessages = codes
			.map((code) => codeToMessage(code))
			.filter((m) => !!m);

		const message = errorMessages.join('. ');

		return message || codeToMessage(ErrorCode.GENERIC);
	};

	const getMutationErrorMessage: ContentContext['getMutationErrorMessage'] = (
		error
	) => {
		if (isAppError(error)) return getErrorMessage(error.stack);
		return getErrorMessage([ErrorCode.GENERIC]);
	};

	return (
		<ContentContext
			value={{
				getContent,
				language,
				setLanguage,
				getErrorMessage,
				getMutationErrorMessage,
				getEntityText: getEntityTextLocal,
			}}
		>
			{children}
		</ContentContext>
	);
};
